import { useEffect, useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import '../marketing.css'

/* The public home page: what Clinic Desk is, for clinics deciding whether to
 * use it, plus the Android app download. Shown at `/` to signed-out visitors
 * on the web only (signed-in staff get the dashboard; the app skips to sign-in).
 *
 * Everything claimed here is something the product does today. No customer
 * logos, counts or testimonials until there are real ones to show. */

/** Published next to the APK (`/download/app.json`), so a new build needs no redeploy of the site. */
type AppInfo = { version: string; size?: string; sha256?: string; minAndroid?: string; file: string }

const CONTACT = 'https://sahuapps.com/'

function Icon({ d, size = 22 }: { d: string; size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"
      strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d={d} /></svg>
  )
}

const I = {
  cal: 'M4 5m0 2a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2h-12a2 2 0 0 1-2-2zM16 3l0 4M8 3l0 4M4 11l16 0',
  users: 'M9 7m-4 0a4 4 0 1 0 8 0a4 4 0 1 0-8 0M3 21v-2a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v2M16 3.13a4 4 0 0 1 0 7.75M21 21v-2a4 4 0 0 0-3-3.85',
  steth: 'M6 4h-1a2 2 0 0 0-2 2v3.5h0a5.5 5.5 0 0 0 11 0v-3.5a2 2 0 0 0-2-2h-1M8 15a6 6 0 1 0 12 0v-3M11 3v2M6 3v2M20 10m-2 0a2 2 0 1 0 4 0a2 2 0 1 0-4 0',
  rupee: 'M18 5h-11h3a4 4 0 0 1 0 8h-3l6 6M7 9l11 0',
  tube: 'M20 8.04l-12.122 12.124a2.857 2.857 0 1 1-4.041-4.04l12.122-12.124M7 13h8M19 15l1.5 1.6a2 2 0 1 1-3 0l1.5-1.6zM15 3l6 6',
  chart: 'M3 3v18h18M7 15l4-4l3 3l5-6',
  shield: 'M12 3a12 12 0 0 0 8.5 3a12 12 0 0 1-8.5 15a12 12 0 0 1-8.5-15a12 12 0 0 0 8.5-3',
  lock: 'M5 11m0 2a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v6a2 2 0 0 1-2 2h-10a2 2 0 0 1-2-2zM8 11v-4a4 4 0 1 1 8 0v4',
  db: 'M12 6m-8 0a8 3 0 1 0 16 0a8 3 0 1 0-16 0M4 6v6a8 3 0 0 0 16 0v-6M4 12v6a8 3 0 0 0 16 0v-6',
  building: 'M3 21h18M5 21v-14l7-4l7 4v14M9 21v-6h6v6M9 10h.01M15 10h.01',
  phone: 'M6 3m0 2a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-8a2 2 0 0 1-2-2zM11 4h2M12 17v.01',
  chat: 'M3 21l1.65-3.8a9 9 0 1 1 3.4 2.9l-5.05.9',
  check: 'M5 12l5 5l10-10',
  arrow: 'M5 12h14M13 6l6 6l-6 6',
  globe: 'M12 12m-9 0a9 9 0 1 0 18 0a9 9 0 1 0-18 0M3.6 9h16.8M3.6 15h16.8M11.5 3a17 17 0 0 0 0 18M12.5 3a17 17 0 0 1 0 18',
  box: 'M12 3l8 4.5v9L12 21l-8-4.5v-9L12 3zM12 12l8-4.5M12 12v9M12 12L4 7.5',
}

/* ---------- small product mock-ups, drawn in markup so they stay sharp ---------- */

function PhoneMock({ variant = 'queue' }: { variant?: 'queue' | 'ticket' }) {
  if (variant === 'ticket') return <TicketPhone />
  return (
    <div className="mk-phone" aria-label="The Clinic Desk app showing today's queue">
      <div className="mk-phone-screen">
        <div className="mk-ph-head">
          <div>
            <div className="mk-ph-small">Wed 7 October</div>
            <div className="mk-ph-title">Good morning</div>
          </div>
          <span className="mk-ph-av">AK</span>
        </div>
        <div className="mk-ph-stats">
          <div><b>18</b><span>Booked</span></div>
          <div><b>4</b><span>Waiting</span></div>
          <div><b>9</b><span>Seen</span></div>
        </div>
        <div className="mk-ph-bar"><i style={{ width: '50%' }} /></div>
        <div className="mk-ph-label">Now serving</div>
        <div className="mk-ph-card mk-ph-live">
          <span className="mk-ph-tk">4</span>
          <div><b>Rahul Sharma</b><span>General Medicine</span></div>
          <em>Live</em>
        </div>
        <div className="mk-ph-label">Up next</div>
        {[['5', 'Ananya Pawar', 'Follow-up', 'Due ₹500', 'due'], ['6', 'Meera Iyer', 'Cardiology', 'Paid', 'ok'], ['7', 'Arjun Deshmukh', 'Cardiology', 'Due ₹1,000', 'due']].map(([t, n, s, p, k]) => (
          <div className="mk-ph-card" key={t}>
            <span className="mk-ph-tk mk-ph-tk-soft">{t}</span>
            <div><b>{n}</b><span>{s}</span></div>
            <em className={k === 'ok' ? 'mk-ok' : 'mk-due'}>{p}</em>
          </div>
        ))}
        <div className="mk-ph-tabs">
          {[I.cal, I.users, I.steth, I.tube].map((d, i) => (
            <span key={d} className={i === 1 ? 'on' : ''}><Icon d={d} size={18} /></span>
          ))}
        </div>
      </div>
    </div>
  )
}

function TicketPhone() {
  return (
    <div className="mk-phone" aria-label="A booking ticket in the Clinic Desk app">
      <div className="mk-phone-screen mk-tix">
        <div className="mk-tix-ok"><Icon d={I.check} size={22} /></div>
        <div className="mk-tix-h">You're booked</div>
        <div className="mk-tix-s">Ticket sent on WhatsApp</div>
        <div className="mk-tix-card">
          <div className="mk-tix-doc"><span>AD</span><div><b>Dr. Arjun Deshpande</b><small>Cardiology</small></div></div>
          <div className="mk-tix-grid">
            <div><small>Patient</small><b>Meera Iyer</b></div>
            <div><small>Date</small><b>Thu 8 Oct</b></div>
            <div><small>Time</small><b>10:00 am</b></div>
            <div><small>Fee</small><b>₹1,000</b></div>
          </div>
          <div className="mk-tix-cut" />
          <div className="mk-tix-foot"><div><small>Token</small><b>07</b></div><div><small>Arrive by</small><b>9:50 am</b></div></div>
        </div>
      </div>
    </div>
  )
}

function QueueMock() {
  return (
    <div className="mk-mini mk-mini-list">
      {[['4', 'Rahul Sharma', 'In consult', 100, 'live'], ['5', 'Ananya Pawar', 'Waiting 4 min', 14, ''], ['6', 'Meera Iyer', 'Waiting 9 min', 30, ''], ['7', 'Arjun Deshmukh', 'Waiting 22 min', 74, 'late']].map(([t, n, w, pct, k]) => (
        <div key={t as string} className="mk-q-row">
          <span className={`mk-q-tk ${k}`}>{t}</span>
          <div><b>{n}</b><i><em style={{ width: `${pct}%` }} className={k as string} /></i></div>
          <small>{w}</small>
        </div>
      ))}
    </div>
  )
}

function SlotMock() {
  const slots: [string, 'free' | 'on' | 'busy'][] = [['9:30', 'free'], ['10:00', 'on'], ['10:30', 'busy'], ['11:00', 'free'], ['11:30', 'free'], ['12:00', 'busy']]
  return (
    <div className="mk-mini">
      <div className="mk-mini-days">
        {[['Wed', '7'], ['Thu', '8'], ['Fri', '9'], ['Sat', '10']].map(([d, n], i) => (
          <span key={n} className={i === 1 ? 'on' : ''}><small>{d}</small><b>{n}</b></span>
        ))}
      </div>
      <div className="mk-mini-slots">
        {slots.map(([t, s]) => <span key={t} className={s}>{t}</span>)}
      </div>
    </div>
  )
}

function StockMock() {
  return (
    <div className="mk-mini mk-mini-list">
      {[['EDTA tube', '96 pcs', 80], ['Glucose strip', '140 strips', 70], ['HbA1c cartridge', '6 pcs', 18], ['Stool container', 'Out', 0]].map(([n, q, w]) => (
        <div key={n as string} className="mk-stock-row">
          <span>{n}</span>
          <i><em style={{ width: `${w}%` }} className={(w as number) < 25 ? 'low' : ''} /></i>
          <b className={(w as number) < 25 ? 'low' : ''}>{q}</b>
        </div>
      ))}
    </div>
  )
}

function RxMock() {
  return (
    <div className="mk-mini mk-rx">
      <div className="mk-rx-alert">Allergic to Penicillin</div>
      <div className="mk-rx-vitals">
        <span><small>BP</small><b>128/82</b></span>
        <span><small>Pulse</small><b>76</b></span>
        <span><small>Sugar</small><b>142</b></span>
      </div>
      <div className="mk-rx-line"><b>Tab. Metformin 500 mg</b><span>1 – 0 – 1 · after food · 30 days</span></div>
    </div>
  )
}

function PayMock() {
  return (
    <div className="mk-mini mk-pay">
      {[['Cash', '₹8,400', 62], ['UPI', '₹4,600', 34], ['Card', '₹500', 4]].map(([m, v, w]) => (
        <div key={m as string} className="mk-pay-row"><span>{m}</span><i><em style={{ width: `${w}%` }} /></i><b>{v}</b></div>
      ))}
      <div className="mk-pay-total"><span>Collected today</span><b>₹13,500</b></div>
    </div>
  )
}

/* ---------- content ---------- */

const ROLES: { id: string; label: string; title: string; points: string[]; icon: string }[] = [
  { id: 'reception', label: 'Reception', icon: I.users, title: 'A calm front desk, even on a Monday morning',
    points: ['Book a doctor or a lab test in three taps, with slots that can never double-book', 'Check patients in and hand out token numbers', 'Send the ticket and the fee on WhatsApp', 'Record cash or UPI payments and close the day with a drawer count'] },
  { id: 'doctor', label: 'Doctor', icon: I.steth, title: 'The next patient, already on your screen',
    points: ['Your own queue with allergies and vitals flagged before you call them in', 'Repeat last prescription, advise tests, close the visit', 'Every past visit and lab report on one record', 'Daily reports on visits and collections'] },
  { id: 'lab', label: 'Lab', icon: I.tube, title: 'Every test done, every tube counted',
    points: ['The day’s lab sheet with sample and done status', 'Mark a test done and its items come out of stock', 'Low-stock alerts before you run out', 'Usage and cost per test, so you know what each test really earns'] },
  { id: 'owner', label: 'Owner', icon: I.chart, title: 'Know how the clinic is doing, from anywhere',
    points: ['Visits, collections and outstanding dues by day, doctor and payment mode', 'Staff logins with exactly the access each role needs', 'An audit trail of who changed what', 'Encrypted nightly backups of the whole clinic'] },
]

const FAQ: { q: string; a: ReactNode }[] = [
  { q: 'Do we need to install anything?', a: 'No. Clinic Desk runs in the browser on any computer, and there is an Android app for phones and tablets. Reception, doctors and the lab all work on the same live data.' },
  { q: 'Can patients book on their own?', a: 'Yes. Patients get their own sign-in to book a doctor or a lab test and see their visits and tickets. Reception still sees every booking on the day sheet.' },
  { q: 'Which payments are supported?', a: 'Cash, UPI, card and insurance are all recorded against the visit or bill. Every payment gets a receipt, and the day-end screen reconciles the drawer by payment mode.' },
  { q: 'Is our patient data kept separate from other clinics?', a: 'Yes. Each clinic’s records are isolated by the database itself, not just by the screens. Staff see only their own clinic, and each role sees only what it needs.' },
  { q: 'What languages does it speak?', a: 'The mobile app is available in English, Hindi, Marathi and Odia. Prescriptions, receipts and invoices print in English.' },
  { q: 'Can we run more than one branch?', a: 'Yes. Each branch runs as its own clinic, with its own doctors, staff, rates and records kept apart.' },
]

export default function Home() {
  const [app, setApp] = useState<AppInfo | null>(null)
  const [role, setRole] = useState('reception')
  const [menu, setMenu] = useState(false)

  useEffect(() => {
    let live = true
    fetch('/download/app.json', { cache: 'no-store' })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => { if (live && j && typeof j.version === 'string' && typeof j.file === 'string') setApp(j) })
      .catch(() => { /* no app published yet: the page says it is coming soon */ })
    return () => { live = false }
  }, [])

  const current = ROLES.find((r) => r.id === role) ?? ROLES[0]!
  const close = () => setMenu(false)

  return (
    <div className="mk">
      <header className="mk-nav">
        <div className="mk-wrap mk-nav-in">
          <a href="#top" className="mk-logo" onClick={close}>
            <span className="mk-logo-mark"><Icon d="M12 5v14M5 12h14" size={18} /></span>
            Clinic Desk
          </a>
          <nav className={`mk-links${menu ? ' open' : ''}`} aria-label="Main">
            <a href="#features" onClick={close}>Features</a>
            <a href="#how" onClick={close}>How it works</a>
            <a href="#teams" onClick={close}>For your team</a>
            <a href="#security" onClick={close}>Security</a>
            <a href="#app" onClick={close}>App</a>
            <a href="#faq" onClick={close}>FAQ</a>
            <Link to="/login" className="mk-links-signin">Sign in</Link>
          </nav>
          <div className="mk-nav-cta">
            <Link to="/login" className="mk-btn mk-btn-ghost mk-hide-sm">Sign in</Link>
            <a href="#contact" className="mk-btn mk-btn-primary">Book a demo</a>
            <button type="button" className="mk-burger" aria-label={menu ? 'Close menu' : 'Open menu'} aria-expanded={menu} onClick={() => setMenu(!menu)}>
              <Icon d={menu ? 'M18 6l-12 12M6 6l12 12' : 'M4 7h16M4 12h16M4 17h16'} />
            </button>
          </div>
        </div>
      </header>

      <main id="top">
        {/* ---------- hero ---------- */}
        <section className="mk-hero">
          <div className="mk-hero-glow" aria-hidden="true" />
          <div className="mk-wrap mk-hero-in">
            <div className="mk-hero-copy">
              <span className="mk-eyebrow"><i />Built for Indian clinics</span>
              <h1>Your front desk, consulting room and lab, <span>in one app.</span></h1>
              <p className="mk-lead">Clinic Desk books patients into real time slots, runs the token queue, puts the next patient’s history in front of the doctor, and keeps the lab stocked. On the web and on Android.</p>
              <div className="mk-hero-cta">
                <a href="#contact" className="mk-btn mk-btn-primary mk-btn-lg">Book a free demo <Icon d={I.arrow} size={18} /></a>
                <a href="#app" className="mk-btn mk-btn-ghost mk-btn-lg">Get the Android app</a>
              </div>
              <ul className="mk-hero-ticks">
                <li><Icon d={I.check} size={16} />No installs for the desk</li>
                <li><Icon d={I.check} size={16} />Cash, UPI and QR</li>
                <li><Icon d={I.check} size={16} />English, हिन्दी, मराठी, ଓଡ଼ିଆ</li>
              </ul>
            </div>
            <div className="mk-hero-art">
              <PhoneMock />
              <div className="mk-float mk-float-a">
                <span className="mk-float-ic mk-ic-teal"><Icon d={I.check} size={18} /></span>
                <div><b>Booking confirmed</b><span>Token 07 · Thu 10:00 am</span></div>
              </div>
              <div className="mk-float mk-float-b">
                <span className="mk-float-ic mk-ic-amber"><Icon d={I.box} size={18} /></span>
                <div><b>HbA1c cartridges low</b><span>6 left · reorder at 10</span></div>
              </div>
              <div className="mk-float mk-float-c">
                <span className="mk-float-ic mk-ic-green"><Icon d={I.chat} size={18} /></span>
                <div><b>Ticket sent on WhatsApp</b><span>to Meera Iyer</span></div>
              </div>
            </div>
          </div>
        </section>

        {/* ---------- strip ---------- */}
        <section className="mk-strip" aria-label="Highlights">
          <div className="mk-wrap mk-strip-in">
            {[[I.cal, 'Slot booking'], [I.users, 'Token queue'], [I.steth, 'Doctor console'], [I.rupee, 'UPI and cash'], [I.tube, 'Lab and stock'], [I.chat, 'WhatsApp tickets']].map(([d, t]) => (
              <span key={t}><Icon d={d!} size={18} />{t}</span>
            ))}
          </div>
        </section>

        {/* ---------- features ---------- */}
        <section id="features" className="mk-section">
          <div className="mk-wrap">
            <div className="mk-head">
              <span className="mk-kicker">Features</span>
              <h2>Everything a clinic does in a day, on one screen</h2>
              <p>From the first phone call to the last receipt, each step is one or two taps, and every role sees the same live day.</p>
            </div>
            <div className="mk-bento">
              <article className="mk-tile mk-tile-wide">
                <div className="mk-tile-copy">
                  <span className="mk-tile-ic"><Icon d={I.cal} /></span>
                  <h3>Booking that never double-books</h3>
                  <p>Each doctor’s hours become real slots. Reception and patients pick a free one; a slot taken a second ago is refused, with the nearest free times offered instead.</p>
                </div>
                <SlotMock />
              </article>
              <article className="mk-tile">
                <div className="mk-tile-copy">
                  <span className="mk-tile-ic"><Icon d={I.users} /></span>
                  <h3>A live token queue</h3>
                  <p>Check-in gives a token. The desk and the doctor watch the same queue, with waiting time and who still owes the fee.</p>
                </div>
                <QueueMock />
              </article>
              <article className="mk-tile">
                <div className="mk-tile-copy">
                  <span className="mk-tile-ic"><Icon d={I.steth} /></span>
                  <h3>The doctor’s console</h3>
                  <p>Allergies, vitals and the last visit before the patient sits down. Repeat a prescription in one tap.</p>
                </div>
                <RxMock />
              </article>
              <article className="mk-tile">
                <div className="mk-tile-copy">
                  <span className="mk-tile-ic"><Icon d={I.tube} /></span>
                  <h3>Lab tests and stock</h3>
                  <p>Book tests with their preparation notes. Marking a test done takes its tubes and reagents out of stock.</p>
                </div>
                <StockMock />
              </article>
              <article className="mk-tile">
                <div className="mk-tile-copy">
                  <span className="mk-tile-ic"><Icon d={I.rupee} /></span>
                  <h3>Billing that adds up</h3>
                  <p>Fees come from each doctor’s rate card. Record cash, UPI or card, print a receipt, raise a bill, and count the drawer against the day.</p>
                </div>
                <PayMock />
              </article>
            </div>
          </div>
        </section>

        {/* ---------- how ---------- */}
        <section id="how" className="mk-section mk-section-tint">
          <div className="mk-wrap">
            <div className="mk-head">
              <span className="mk-kicker">How it works</span>
              <h2>Set up in three steps</h2>
            </div>
            <ol className="mk-steps">
              <li>
                <span className="mk-step-n">1</span>
                <h3>Set up your clinic</h3>
                <p>Add your doctors, their hours and fees, your lab tests and prices. Import your stock list in one paste.</p>
              </li>
              <li>
                <span className="mk-step-n">2</span>
                <h3>Give each person a login</h3>
                <p>Reception, doctors and the lab each get the screens their job needs, and nothing else.</p>
              </li>
              <li>
                <span className="mk-step-n">3</span>
                <h3>Run the day</h3>
                <p>Bookings, the queue, consultations, lab tests and payments flow through one live record.</p>
              </li>
            </ol>
          </div>
        </section>

        {/* ---------- roles ---------- */}
        <section id="teams" className="mk-section">
          <div className="mk-wrap">
            <div className="mk-head">
              <span className="mk-kicker">For your team</span>
              <h2>Made for the way each person works</h2>
            </div>
            <div className="mk-roles">
              <div className="mk-role-tabs" role="tablist" aria-label="Roles">
                {ROLES.map((r) => (
                  <button key={r.id} type="button" role="tab" aria-selected={r.id === role}
                    className={r.id === role ? 'on' : ''} onClick={() => setRole(r.id)}>
                    <Icon d={r.icon} size={18} />{r.label}
                  </button>
                ))}
              </div>
              <div className="mk-role-panel" role="tabpanel">
                <h3>{current.title}</h3>
                <ul>
                  {current.points.map((p) => <li key={p}><span><Icon d={I.check} size={14} /></span>{p}</li>)}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* ---------- security ---------- */}
        <section id="security" className="mk-section mk-dark">
          <div className="mk-wrap">
            <div className="mk-head mk-head-dark">
              <span className="mk-kicker">Security and privacy</span>
              <h2>Patient records deserve more than a password</h2>
              <p>Clinic Desk is built for medical data from the database up.</p>
            </div>
            <div className="mk-sec-grid">
              {[
                [I.building, 'Each clinic is sealed off', 'Records are isolated per clinic by the database itself, so one clinic can never see another’s patients.'],
                [I.shield, 'Access by role', 'Reception, doctors, lab and admins each see only what their work needs.'],
                [I.db, 'Encrypted nightly backups', 'The whole clinic is backed up every night, encrypted so the copies cannot be read on the server.'],
                [I.lock, 'Every change is logged', 'An audit trail records who changed a bill, a booking or a record, and when.'],
              ].map(([d, t, b]) => (
                <div key={t} className="mk-sec">
                  <span className="mk-sec-ic"><Icon d={d!} /></span>
                  <h3>{t}</h3>
                  <p>{b}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ---------- app ---------- */}
        <section id="app" className="mk-section">
          <div className="mk-wrap mk-app">
            <div className="mk-app-copy">
              <span className="mk-kicker">Mobile</span>
              <h2>The whole clinic in your pocket</h2>
              <p>The Android app works for reception, doctors, the lab and patients, signed in the same way as the website. Choose English, Hindi, Marathi or Odia.</p>
              {app ? (
                <>
                  <div className="mk-app-cta">
                    <a className="mk-btn mk-btn-primary mk-btn-lg" href={`/download/${app.file}`} download>
                      <Icon d={I.phone} size={18} /> Download for Android
                    </a>
                  </div>
                  <p className="mk-meta">Version {app.version}{app.size ? ` · ${app.size}` : ''} · Android {app.minAndroid ?? '7.0'} or newer</p>
                  <details className="mk-steps-small">
                    <summary>How to install the APK</summary>
                    <ol>
                      <li>Open this page on your Android phone and tap <b>Download for Android</b>.</li>
                      <li>Open the downloaded file. If asked, allow your browser to <b>install unknown apps</b>.</li>
                      <li>Tap <b>Install</b>, open Clinic Desk and sign in.</li>
                    </ol>
                    {app.sha256 && <p className="mk-hash">SHA-256: {app.sha256}</p>}
                  </details>
                </>
              ) : (
                <p className="mk-meta">The Android app is on its way. Until then, open Clinic Desk in your phone’s browser.</p>
              )}
              <div className="mk-soon">
                <span><b>Google Play</b> · coming soon</span>
                <span><b>iPhone</b> · coming soon, use Safari for now</span>
              </div>
            </div>
            <div className="mk-app-art"><PhoneMock variant="ticket" /></div>
          </div>
        </section>

        {/* ---------- faq ---------- */}
        <section id="faq" className="mk-section mk-section-tint">
          <div className="mk-wrap mk-faq-wrap">
            <div className="mk-head mk-head-left">
              <span className="mk-kicker">FAQ</span>
              <h2>Questions clinics ask us</h2>
              <p>Something else on your mind? <a href="#contact">Talk to us.</a></p>
            </div>
            <div className="mk-faq">
              {FAQ.map((f) => (
                <details key={f.q}>
                  <summary>{f.q}<span aria-hidden="true">+</span></summary>
                  <p>{f.a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* ---------- cta ---------- */}
        <section id="contact" className="mk-section">
          <div className="mk-wrap">
            <div className="mk-cta">
              <div>
                <h2>See Clinic Desk with your own clinic’s day</h2>
                <p>We will set it up with your doctors, timings and tests, and walk your front desk through it.</p>
              </div>
              <div className="mk-cta-btns">
                <a href={CONTACT} target="_blank" rel="noopener noreferrer" className="mk-btn mk-btn-light mk-btn-lg">Book a free demo <Icon d={I.arrow} size={18} /></a>
                <Link to="/login" className="mk-btn mk-btn-outline-light mk-btn-lg">Sign in</Link>
              </div>
            </div>
          </div>
        </section>
      </main>

      <footer className="mk-foot">
        <div className="mk-wrap mk-foot-in">
          <div>
            <a href="#top" className="mk-logo"><span className="mk-logo-mark"><Icon d="M12 5v14M5 12h14" size={18} /></span>Clinic Desk</a>
            <p>Appointments, the OPD queue, consultations, billing, lab and patient records for clinics.</p>
          </div>
          <nav aria-label="Footer">
            <a href="#features">Features</a>
            <a href="#security">Security</a>
            <a href="#app">Android app</a>
            <a href="#faq">FAQ</a>
            <Link to="/login">Sign in</Link>
          </nav>
        </div>
        <div className="mk-wrap mk-foot-base">
          <span>© {new Date().getFullYear()} Clinic Desk</span>
          <span>Developed by <a href="https://sahuapps.com/" target="_blank" rel="noopener noreferrer">SahuApps</a></span>
        </div>
      </footer>
    </div>
  )
}
